import { CROSS_DOMAIN_CAPABILITIES, DOMAINS, type Action, type Capability, type CurrentUser, type Domain, type Role } from "@rrid/shared-types";

/** True if the user can perform `action` in at least one domain (for nav/portal gating). */
export function canAnyDomain(
  check: (action: Action, ctx?: { domain?: Domain | null }) => boolean,
  action: Action,
): boolean {
  return DOMAINS.some((d) => check(action, { domain: d }));
}

const ROLE_CAPS: Record<Role, Capability[]> = {
  Student: ["student"],
  "Quality Checker in Training": ["jrQC"],
  Manager: ["ae", "srQC", "ops"],
  Central: ["eic", "srQC"],
  Admin: ["admin"],
};

/** Header subtitle: cross-domain roles show "all domains"; else the user's domains. */
export function domainLabelForUser(user: Pick<CurrentUser, "roles" | "domains">): string {
  const caps = new Set(user.roles.flatMap((r) => ROLE_CAPS[r] ?? []));
  if ([...caps].some((c) => CROSS_DOMAIN_CAPABILITIES.has(c))) return "all domains";
  if (user.domains.length === 0) return "no domain";
  return user.domains.join(", ");
}

/** Header line under the user's name: "Student · SSH" or "Manager · all domains". */
export function userRoleSubtitle(user: Pick<CurrentUser, "roles" | "domains">): string {
  return `${user.roles.join(", ")} · ${domainLabelForUser(user)}`;
}

export function humanizeNotificationKind(kind: string): string {
  const map: Record<string, string> = {
    "coi.completed": "COI completed",
    "coi.conflict": "COI conflict",
    coi: "COI",
  };
  return map[kind] ?? kind.replace(/[._]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
